import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import api, { getSavedLeaseAbstractionResult } from '../api';
import Tooltip from './Tooltip';
import { LeaseAbstractionSettings } from './LeaseAbstractionSettings'; // Import the settings component
import { showConfirm, showAlert } from '../utils/ModalService';


// Minimal property interface for the dropdown
interface Property {
  id: string;
  address: string;
  custom_id?: string;
}

interface LeaseExtractedData {
  full_text?: string;
  lease_start_date?: string;
  lease_end_date?: string;
  rent_amount?: number;
  security_deposit?: number;
  tenant_name?: string;
  landlord_name?: string;
  property_address?: string;
  gcs_uri?: string;
}

interface Lease {
  id: string;
  property_id: string;
  extracted_data?: LeaseExtractedData;
  status: string;
}

export const LeaseAbstraction: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [propertyId, setPropertyId] = useState('');
  const [message, setMessage] = useState('');
  const [extractedLease, setExtractedLease] = useState<Lease | null>(null);
  const [leases, setLeases] = useState<Lease[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<LeaseExtractedData | null>(null);
  const [savedAbstraction, setSavedAbstraction] = useState<Lease | null>(null); // New state for saved abstraction

  useEffect(() => {
    fetchProperties();
    fetchLeases();
  }, []);

  const fetchLeases = async () => {
    try {
      const response = await api.get('/leases');
      console.log('Fetched leases:', response.data); // Add this line
      setLeases(response.data);
    } catch (error) {
      console.error('Failed to fetch leases', error);
      setMessage('Could not load leases.');
    }
  };

  const handleViewDocument = async (leaseId: string) => {
    setMessage('Opening document...');
    try {
      const response = await api.get(`/leases/${leaseId}/view`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] as string }));
      window.open(url, '_blank');
      setMessage('');
    } catch (error: any) {
      console.error('Error viewing document:', error.response?.data || error.message);
      setMessage('Could not open document. Please ensure you are logged in.');
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await api.get('/properties');
      setProperties(response.data);
    } catch (error) {
      console.error('Failed to fetch properties for dropdown', error);
      setMessage('Could not load properties for the dropdown.');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    } else {
      setSelectedFile(null);
    }
  };

  const handleUpload = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedFile || !propertyId) {
      setMessage('Please select a property and a file.');
      return;
    }

    setMessage('Uploading and abstracting lease...');
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('property_id', propertyId);

    try {
      const response = await api.post(`/leases/upload`, formData);
      console.log('API Response:', response);
      if (response.status === 201 || response.status === 200) {
        setMessage(response.data?.message || 'Lease abstraction completed.');
        const leaseData = response.data?.lease;
        setExtractedLease(leaseData?.lease || null);
        setSavedAbstraction(null); // Clear saved abstraction to ensure new data is displayed
        setSelectedFile(null);
        setPropertyId('');
        fetchLeases(); // Refresh the list of leases
      } else {
        setMessage(`Unexpected response status: ${response.status}. Please check server logs.`);
        setExtractedLease(null);
      }
    } catch (error) {
      console.error('An error occurred during lease abstraction.', error);
      setMessage(`An error occurred during lease abstraction: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setExtractedLease(null);
    }
  };



  useEffect(() => {
    const loadSavedAbstraction = async () => {
        if (!propertyId || !extractedLease?.id) return;
        try {
            const saved = await getSavedLeaseAbstractionResult(propertyId, extractedLease.id);
            if (saved) {
                setSavedAbstraction(saved);
            }
        } catch (err) {
            console.error('Error loading saved lease abstraction:', err);
        }
    };
    loadSavedAbstraction();
  }, [propertyId, extractedLease?.id]);

  const displayLease = extractedLease;

  const handleApprove = async () => {
    if (!extractedLease) return;

    try {
      const response = await api.put(`/leases/${extractedLease.id}/status/${'active'}`);
      if (response.status === 200) {
        setMessage('Lease approved successfully!');
        fetchLeases(); // Refresh the list to show updated status
        setExtractedLease(null); // Clear selected lease after approval
      } else {
        setMessage(`Failed to approve lease: ${response.status}`);
      }
    } catch (error) {
      console.error('Error approving lease:', error);
      setMessage(`Error approving lease: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedData(extractedLease?.extracted_data || {});
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Optionally, revert changes by re-fetching the lease or resetting extractedLease state
    if (extractedLease) {
      setExtractedLease(leases.find(lease => lease.id === extractedLease.id) || null);
    }
  };

  const handleSave = async () => {
    if (!extractedLease || !editedData) return;

    try {
      const updatePayload = {
        property_id: extractedLease.property_id, // Keep property_id as it's required by the backend model
        extracted_data: editedData,
      };

      const response = await api.put(`/leases/${extractedLease.id}`, updatePayload);
      if (response.status === 200) {
        setMessage('Lease updated successfully!');
        setIsEditing(false);
        fetchLeases(); // Refresh the list to show updated data
        setExtractedLease(response.data.lease); // Update the displayed lease with the new data
      } else {
        setMessage(`Failed to update lease: ${response.status}`);
      }
    } catch (error) {
      console.error('Error saving lease:', error);
      setMessage(`Error saving lease: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteLease = async () => {
    if (!extractedLease) return;
    showConfirm('Confirm Action', 'Are you sure you want to delete this lease? This action cannot be undone.', async () => {
      try {
        await api.delete(`/leases/${extractedLease.id}`);
        setMessage('Lease deleted successfully!');
        setExtractedLease(null); // Clear the displayed lease
        fetchLeases(); // Refresh the list of leases
      } catch (error) {
        console.error('Error deleting lease:', error);
        setMessage(`Error deleting lease: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    });
  };

  return (
    <div className="container mx-auto p-4 bg-bg-primary min-h-screen">
      <LeaseAbstractionSettings /> {/* Render the settings component here */}

      <div className="flex items-center mb-4">
        <h2 className="text-2xl font-bold text-text-secondary">{t("leaseAbstraction.k1")}</h2>
        <Tooltip text="This page allows you to upload a single lease document to be automatically analyzed by our AI. The system will extract key information from the document, which you can then review, edit, and approve to create a new lease record in your portfolio." />
      </div>

      {message && <p className={message.includes('successfully') ? 'text-success mb-4' : 'text-danger mb-4'}>{message}</p>}

      <form onSubmit={handleUpload} className="mb-6 bg-bg-secondary p-4 rounded shadow border border-border-primary">
        <div className="mb-4">
          <label htmlFor="propertyId" className="block text-text-secondary text-sm font-bold mb-2">{t("leaseAbstraction.k2")}</label>
          <select
            id="propertyId"
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            required
            className="shadow appearance-none border border-border-primary rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          >
            <option value="" disabled>{t("leaseAbstraction.k3")}</option>
            {properties.map((prop) => (
              <option key={prop.id} value={prop.id}>
                {prop.custom_id ? `${prop.custom_id} (${prop.address})` : prop.address}
              </option>
            ))}
          </select>
        </div>
        <div className="mb-4">
          <label htmlFor="leaseFile" className="block text-text-secondary text-sm font-bold mb-2">{t("leaseAbstraction.k4")}</label>
          <input
            type="file"
            id="leaseFile"
            accept=".pdf,.png,.jpeg,.jpg"
            onChange={handleFileChange}
            required
            className="shadow appearance-none border border-border-primary rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <button type="submit" className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark">
          {t("leaseAbstraction.k5")} </button>
      </form>

      <div className="mt-6">
        <h3 className="text-xl font-bold mb-4 text-text-secondary">{t("leaseAbstraction.k6")}</h3>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-1/3 border-r border-border-primary pr-4">
            {leases.map(lease => (
              <div key={lease.id} className="mb-2 p-3 border border-border-primary rounded cursor-pointer bg-bg-secondary hover:bg-bg-tertiary" onClick={() => setExtractedLease(lease)}>
                <p className="text-text-primary"><strong>{t("leaseAbstraction.k7")}</strong> {lease.id}</p>
                <p className="text-text-secondary"><strong>{t("leaseAbstraction.k8")}</strong> {lease.status}</p>
              </div>
            ))}
          </div>
          <div className="w-full md:w-2/3 pl-4">
            {displayLease && (
              <div className="border border-border-primary p-4 rounded shadow bg-bg-secondary">
                <h3 className="text-xl font-bold mb-4 text-text-secondary">{t("leaseAbstraction.k9")} {displayLease.id})</h3>
                <div className="mb-4 text-right space-x-2">
                  {!isEditing && displayLease.status === 'pending_review' && (
                    <button
                      onClick={handleApprove}
                      className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark"
                    >
                      {t("leaseAbstraction.k10")} </button>
                  )}
                  {!isEditing && (
                    <button
                      onClick={handleEdit}
                      className="bg-success text-white px-4 py-2 rounded hover:bg-success-dark"
                    >
                      {t("leaseAbstraction.k11")} </button>
                  )}
                  {!isEditing && (
                    <button
                      onClick={handleDeleteLease}
                      className="bg-danger text-white px-4 py-2 rounded hover:bg-red-700"
                    >
                      {t("leaseAbstraction.k12")} </button>
                  )}
                  {isEditing && (
                    <>
                      <button
                        onClick={handleSave}
                        className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark"
                      >
                        {t("leaseAbstraction.k13")} </button>
                      <button
                        onClick={handleCancel}
                        className="bg-transparent border border-border-primary text-text-primary px-4 py-2 rounded hover:bg-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-border-primary"
                      >
                        {t("leaseAbstraction.k14")} </button>
                    </>
                  )}
                </div>
                <p><strong>{t("leaseAbstraction.k15")}</strong> {displayLease.property_id}</p>
                <p><strong>{t("leaseAbstraction.k16")}</strong> 
                  <button 
                    onClick={() => handleViewDocument(displayLease.id)}
                    className="ml-2 text-primary hover:underline bg-transparent border-none p-0 cursor-pointer"
                  >
                    {t("leaseAbstraction.k17")} </button>
                </p>
                <p><strong>{t("leaseAbstraction.k8")}</strong> {displayLease.status}</p>
                
                <h4 className="text-lg font-semibold mt-4 mb-2 text-text-secondary">{t("leaseAbstraction.k18")}</h4>
                {displayLease.extracted_data ? (
                  <ul className="list-none p-0">
                    {displayLease.extracted_data.lease_start_date && (
                      <li className="mb-1 pb-1 border-b border-border-primary">
                        <strong>{t("leaseAbstraction.k19")}</strong> {
                          isEditing ? (
                            <input
                              type="text"
                              value={editedData?.lease_start_date || ''}
                              onChange={(e) => setEditedData({ ...editedData, lease_start_date: e.target.value })}
                              className="w-full p-1 border border-border-primary rounded"
                            />
                          ) : (
                            displayLease.extracted_data.lease_start_date && new Date(displayLease.extracted_data.lease_start_date).toLocaleDateString('en-US')
                          )
                        }
                      </li>
                    )}
                    {displayLease.extracted_data.lease_end_date && (
                      <li className="mb-1 pb-1 border-b border-border-primary">
                        <strong>{t("leaseAbstraction.k20")}</strong> {
                          isEditing ? (
                            <input
                              type="text"
                              value={editedData?.lease_end_date || ''}
                              onChange={(e) => setEditedData({ ...editedData, lease_end_date: e.target.value })}
                              className="w-full p-1 border border-border-primary rounded"
                            />
                          ) : (
                            displayLease.extracted_data.lease_end_date && new Date(displayLease.extracted_data.lease_end_date).toLocaleDateString('en-US')
                          )
                        }
                      </li>
                    )}
                                        {displayLease.extracted_data.rent_amount && (
                      <li className="mb-1 pb-1 border-b border-border-primary">
                        <strong>{t("leaseAbstraction.k21")}</strong> {
                          isEditing ? (
                            <input
                              type="number"
                              value={editedData?.rent_amount || ''}
                              onChange={(e) => setEditedData({ ...editedData, rent_amount: parseFloat(e.target.value) })}
                              className="w-full p-1 border border-border-primary rounded"
                            />
                          ) : (
                            `${displayLease.extracted_data.rent_amount.toFixed(2)}`
                          )
                        }
                      </li>
                    )}
                    {displayLease.extracted_data.security_deposit && (
                      <li className="mb-1 pb-1 border-b border-border-primary">
                        <strong>{t("leaseAbstraction.k22")}</strong> {
                          isEditing ? (
                            <input
                              type="number"
                              value={editedData?.security_deposit || ''}
                              onChange={(e) => setEditedData({ ...editedData, security_deposit: parseFloat(e.target.value) })}
                              className="w-full p-1 border border-border-primary rounded"
                            />
                          ) : (
                            `${displayLease.extracted_data.security_deposit.toFixed(2)}`
                          )
                        }
                      </li>
                    )}
                    {displayLease.extracted_data.tenant_name && (
                      <li className="mb-1 pb-1 border-b border-border-primary">
                        <strong>{t("leaseAbstraction.k23")}</strong> {
                          isEditing ? (
                            <input
                              type="text"
                              value={editedData?.tenant_name || ''}
                              onChange={(e) => setEditedData({ ...editedData, tenant_name: e.target.value })}
                              className="w-full p-1 border border-border-primary rounded"
                            />
                          ) : (
                            displayLease.extracted_data.tenant_name
                          )
                        }
                      </li>
                    )}
                    {displayLease.extracted_data.landlord_name && (
                      <li className="mb-1 pb-1 border-b border-border-primary">
                        <strong>{t("leaseAbstraction.k24")}</strong> {
                          isEditing ? (
                            <input
                              type="text"
                              value={editedData?.landlord_name || ''}
                              onChange={(e) => setEditedData({ ...editedData, landlord_name: e.target.value })}
                              className="w-full p-1 border border-border-primary rounded"
                            />
                          ) : (
                            displayLease.extracted_data.landlord_name
                          )
                        }
                      </li>
                    )}
                    {displayLease.extracted_data.property_address && (
                      <li className="mb-1 pb-1 border-b border-border-primary">
                        <strong>{t("leaseAbstraction.k25")}</strong> {
                          isEditing ? (
                            <input
                              type="text"
                              value={editedData?.property_address || ''}
                              onChange={(e) => setEditedData({ ...editedData, property_address: e.target.value })}
                              className="w-full p-1 border border-border-primary rounded"
                            />
                          ) : (
                            displayLease.extracted_data.property_address
                          )
                        }
                      </li>
                    )}
                    {/* Add more fields as they are extracted */}
                  </ul>
                ) : (
                  <p className="text-text-secondary">{t("leaseAbstraction.k26")}</p>
                )}

                <h4 className="text-lg font-semibold mt-4 mb-2 text-text-secondary">{t("leaseAbstraction.k27")}</h4>
                <div className="max-h-72 overflow-y-auto border border-border-primary p-3 rounded bg-bg-tertiary">
                  <pre className="whitespace-pre-wrap break-words text-text-secondary">{displayLease.extracted_data?.full_text}</pre>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default LeaseAbstraction;

  
